import AddIcon from '@mui/icons-material/Add'
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'
import DeleteIcon from '@mui/icons-material/Delete'
import DoneIcon from '@mui/icons-material/Done'
import EditIcon from '@mui/icons-material/Edit'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useContext, useEffect, useState, useMemo } from 'react'
import AppContext from '../../../context/appContext'
import {
  useCreateSamplingAreas,
  useDeleteSamplingAreas,
  useDeleteSamplingAreasByFarm,
  useUpdateSamplingAreas,
} from '../../../hooks/data/useSamplingAreas'
import { GeoJSON } from '../../../utils/GeoJSON/index'
import { useProcessLoading } from '../../../utils/Loading/useProcessLoading'
import { miniumFeatureHectareAllowed } from '../../../utils/mapDrawHandle'
import { messages } from '../../../utils/messages/index'
import { LoadingButton } from '../../Button/LoadingButton'
import ClassificationButtons from '../ClassificationPanel'
import ClassificationCreation, { BackButton } from '../Creation'

const StratificationPanel = ({
  samplingAreas,
  samplingAreasFeatures,
  setSamplingAreasFeatures,
  farm,
  perimeter,
  wasSaved,
  setWasSaved,
  georaster,
  setGeoraster,
  drawActivated,
  setDrawActivated,
  setMapAction,
  mapAction,
  setSelectedArea,
  selectedArea,
  setSelectedAreaType,
  setVectorized,
  restratification,
  setRestratification,
  canEditClassification,
  setTabValue,
  supportedCreationModes,
  previewSampling,
  setPreviewSampling,
}) => {
  const [loading, setLoading] = useState(false)
  const { setPartialChanges, setHideNavbar } = useContext(AppContext)

  const { processLoading } = useProcessLoading()

  const { mutateAsync: createSamplingAreas } = useCreateSamplingAreas({ farmId: farm?.id })
  const { mutateAsync: updateSamplingAreas } = useUpdateSamplingAreas({ farmId: farm?.id })
  const { mutateAsync: deleteSamplingAreas } = useDeleteSamplingAreas({ farmId: farm?.id })
  const { mutateAsync: deleteSamplingAreasByFarm } = useDeleteSamplingAreasByFarm({
    farmId: farm?.id,
  })

  function resetControl() {
    setVectorized(false)
    setWasSaved(true)
    setDrawActivated(false)
    setPartialChanges(false)
    setHideNavbar(false)
    setLoading(false)
  }

  const saveStratification = async () => {
    setLoading(true)
    try {
      await createSamplingAreas({ features: samplingAreasFeatures })
      resetControl()
    } catch {
      // Only the spinner recovers: leaving the panel in edit mode keeps the drawn
      // strata on screen instead of claiming a save that never happened. The global
      // snackbar already reports the failure.
      setLoading(false)
    }
  }

  const updateStratification = async () => {
    setLoading(true)

    const entitiesToDelete = (samplingAreas ?? [])
      .filter(
        samplingArea =>
          !samplingAreasFeatures.find(
            strata => samplingArea.properties.id === strata.properties.id,
          ),
      )
      .map(samplingArea => ({ id: samplingArea.properties.id }))
    const featuresToUpdate = samplingAreasFeatures.filter(strata => strata.properties.id)
    const featuresToCreate = samplingAreasFeatures.filter(strata => !strata.properties.id)

    try {
      if (entitiesToDelete.length > 0) await deleteSamplingAreas({ entities: entitiesToDelete })
      if (featuresToUpdate.length > 0) await updateSamplingAreas({ features: featuresToUpdate })
      if (featuresToCreate.length > 0) await createSamplingAreas({ features: featuresToCreate })
      resetControl()
    } catch {
      // A partially applied update must not be reported as saved — stay in edit mode
      // so the operator can retry against the refetched state.
      setLoading(false)
    }
  }

  // Re-syncs the editable features whenever the query data changes, which is what
  // makes the panel reflect a mutation once its invalidation has refetched. Editing
  // (`drawActivated && !wasSaved`) suspends the sync: an update chains delete/update/create
  // mutations that each invalidate and refetch, so syncing mid-chain would erase the strata
  // still waiting to be persisted and leave nothing to retry with if a later step rejects.
  // Both exits from edit mode — resetControl on success and BackButton on cancel — clear the
  // gate, so the final server state always lands.
  useEffect(() => {
    if (!samplingAreas || (drawActivated && !wasSaved)) return

    setSamplingAreasFeatures(samplingAreas)
  }, [samplingAreas, setSamplingAreasFeatures, drawActivated, wasSaved])

  const disableActionReason = useMemo(() => {
    if (mapAction === 'edit') {
      return messages.getMessage(messages.keys.ERROR_CANNOT_EDIT_GEOMETRY_APPLY_CHANGES_MESSAGE)
    }

    if (samplingAreasFeatures.length === 0) {
      return messages.getMessage(
        messages.keys.ERROR_CANNOT_EDIT_SAMPLING_AREA_EMPTY_GEOMETRIES_MESSAGE,
      )
    }

    if (samplingAreasFeatures.some(s => s.properties.name === '')) {
      return messages.getMessage(
        messages.keys.ERROR_CANNOT_EDIT_SAMPLING_AREA_EMPTY_GEOMETRY_NAME_MESSAGE,
      )
    }

    if (
      samplingAreasFeatures.some(
        (s, i) =>
          samplingAreasFeatures.findIndex(s2 => s2.properties.name === s.properties.name) !== i,
      )
    ) {
      return messages.getMessage(
        messages.keys.ERROR_CANNOT_EDIT_SAMPLING_AREA_DUPLICATE_NAMES_NAME_MESSAGE,
      )
    }

    const samplingAreaSizeShortedThanMinimumAllowedIndex = samplingAreasFeatures.findIndex(
      s => GeoJSON.hectareArea(s) < miniumFeatureHectareAllowed,
    )
    if (samplingAreaSizeShortedThanMinimumAllowedIndex !== -1) {
      return messages.getMessage(
        messages.keys.ERROR_CANNOT_EDIT_SAMPLING_AREA_GEOMETRY_LOWER_THAN_MINIMUM_ALLOWED_MESSAGE,
        [
          samplingAreasFeatures[samplingAreaSizeShortedThanMinimumAllowedIndex].properties.name,
          miniumFeatureHectareAllowed,
        ],
      )
    }

    return null
  }, [samplingAreasFeatures, mapAction])

  return (
    <>
      {(loading || !farm) && (
        <Stack
          sx={{
            height: drawActivated ? '75vh' : '85vh',
            overflowY: 'scroll',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Stack>
      )}

      {!loading && farm && (
        <Stack sx={{ height: drawActivated ? '75vh' : '85vh', overflowY: 'scroll' }}>
          {!drawActivated && wasSaved && (
            <ClassificationButtons
              canEditClassification={canEditClassification}
              classificationAlreadyCreatedMessage="Ya se ha realizado la estratificación del establecimiento."
              classificationType="Estratificación"
              deleteClassification={async () => {
                await processLoading({
                  loadingMessage: 'Borrando estratificación...',
                  errorMessage: 'Error al borrar la estratificación de exclusión.',
                  doAction: async () => {
                    await deleteSamplingAreasByFarm()
                    setSamplingAreasFeatures([])
                  },
                })
              }}
              deleteMessageConfirmation="Esta a punto de eliminar todos los estratos creados hasta el momento y no se van a poder recuperar. ¿Desea continuar?"
              editMessageButton="Re-estratificar establecimiento"
              nextStepMessage="Continuar con el Plan de Monitoreo"
              nextStepTabIndex={2}
              setDrawActivated={setDrawActivated}
              setLoading={setLoading}
              setRestratification={setRestratification}
              setTabValue={setTabValue}
              setWasSaved={setWasSaved}
            >
              Borrar estratificación
            </ClassificationButtons>
          )}
          {!drawActivated && !wasSaved && (
            <ClassificationCreation
              georaster={georaster}
              perimeter={perimeter}
              previewSampling={previewSampling}
              setDrawActivated={setDrawActivated}
              setGeoraster={setGeoraster}
              setPreviewSampling={setPreviewSampling}
              setSamplingAreasFeatures={setSamplingAreasFeatures}
              setVectorized={setVectorized}
              supportedCreationModes={supportedCreationModes}
              type="Stratification"
            />
          )}
          {drawActivated && !wasSaved && (
            <>
              <BackButton
                reloadFeatures={() => setSamplingAreasFeatures(samplingAreas ?? [])}
                setDrawActivated={setDrawActivated}
                setMapAction={setMapAction}
                setWasSaved={setWasSaved}
              />
              <Grid container sx={{ padding: '0 10px' }}>
                <Grid item mt={2} sx={{ textAlign: 'center' }} xs={12}>
                  <Box sx={{ padding: '10px', textAlign: 'center' }}>
                    <Button
                      disabled={mapAction === 'edit'}
                      size="small"
                      startIcon={<AddIcon />}
                      variant="outlined"
                      onClick={() => {
                        setSelectedArea(null)
                        setSelectedAreaType('strata')
                        setMapAction('draw')
                      }}
                    >
                      Crear estrato
                    </Button>
                  </Box>
                </Grid>
                {samplingAreasFeatures &&
                  samplingAreasFeatures.map((strata, index) => {
                    return (
                      <Grid
                        key={
                          strata.properties.id || strata.properties.name || `strata-idx-${index}`
                        }
                        item
                        xs={12}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{
                            alignItems: 'center',
                            marginTop: '10px',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box
                            sx={{
                              width: '20px',
                              minWidth: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              backgroundColor: strata.properties.color,
                            }}
                            onClick={() => {
                              setSamplingAreasFeatures(prev =>
                                prev.map((samplingArea, indexPrev) => ({
                                  ...samplingArea,
                                  properties: {
                                    ...samplingArea.properties,
                                    selected: indexPrev === index,
                                  },
                                })),
                              )
                            }}
                          />
                          <TextField
                            error={
                              samplingAreasFeatures.filter(
                                s => s.properties.name === strata.properties.name,
                              ).length > 1 || !strata.properties.name
                            }
                            placeholder="Nombre"
                            value={strata.properties.name}
                            variant="standard"
                            onChange={e => {
                              // Must not write in place: these features are the hook's
                              // memoised projection, and BackButton restores from that same
                              // array when the operator cancels a re-stratification.
                              setSamplingAreasFeatures(prev =>
                                prev.map((feature, featureIndex) =>
                                  featureIndex === index
                                    ? {
                                        ...feature,
                                        properties: {
                                          ...feature.properties,
                                          name: e.target.value,
                                        },
                                      }
                                    : feature,
                                ),
                              )
                              setPartialChanges(true)
                            }}
                          />
                          <Typography variant="p">
                            {strata.properties?.area?.toFixed(2)} ha
                          </Typography>
                          <Stack direction="row" spacing={0} sx={{ alignItems: 'center' }}>
                            <IconButton
                              color="primary"
                              disabled={mapAction === 'edit' && selectedArea !== strata}
                              size="small"
                              sx={{
                                display:
                                  mapAction === 'edit' && selectedArea === strata
                                    ? 'none'
                                    : 'initial',
                              }}
                              variant="contained"
                              onClick={() => {
                                setSelectedArea(strata)
                                setSelectedAreaType('strata')
                                setMapAction('draw')
                              }}
                            >
                              <AddIcon />
                            </IconButton>
                            {mapAction === 'edit' && selectedArea === strata ? (
                              <>
                                <IconButton
                                  color="primary"
                                  size="small"
                                  variant="contained"
                                  onClick={() => {
                                    setSelectedArea(strata)
                                    setSelectedAreaType('strata')
                                    setMapAction('done')
                                  }}
                                >
                                  <DoneIcon />
                                </IconButton>
                                <IconButton
                                  color="primary"
                                  size="small"
                                  variant="contained"
                                  onClick={() => {
                                    setSelectedArea(strata)
                                    setSelectedAreaType('strata')
                                    setMapAction(null)
                                    setPartialChanges(false)
                                  }}
                                >
                                  <ClearOutlinedIcon />
                                </IconButton>
                              </>
                            ) : (
                              <IconButton
                                color="primary"
                                disabled={mapAction === 'edit' && selectedArea !== strata}
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  setSelectedArea(strata)
                                  setSelectedAreaType('strata')
                                  setMapAction('edit')
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            )}
                            <IconButton
                              color="error"
                              disabled={mapAction === 'edit' && selectedArea !== strata}
                              size="small"
                              sx={{
                                display:
                                  mapAction === 'edit' && selectedArea === strata
                                    ? 'none'
                                    : 'initial',
                              }}
                              variant="contained"
                              onClick={() => {
                                setSamplingAreasFeatures(prev => {
                                  const newStratas = prev.filter(s => s !== strata)
                                  return newStratas
                                })
                                setMapAction('remove')
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Grid>
                    )
                  })}
              </Grid>
            </>
          )}
        </Stack>
      )}

      {drawActivated && samplingAreasFeatures && !wasSaved && !restratification && (
        <Box sx={{ paddingTop: '20px', width: '100%', textAlign: 'center' }}>
          <LoadingButton
            disableActionReason={disableActionReason}
            loading={loading}
            onClick={saveStratification}
          >
            Guardar Estratificación
          </LoadingButton>
        </Box>
      )}

      {drawActivated && samplingAreasFeatures && !wasSaved && restratification && (
        <Box sx={{ paddingTop: '20px', width: '100%', textAlign: 'center' }}>
          <LoadingButton
            disableActionReason={disableActionReason}
            loading={loading}
            onClick={updateStratification}
          >
            Actualizar Estratificación
          </LoadingButton>
        </Box>
      )}
    </>
  )
}

export default StratificationPanel
